import React from 'react';
import styles from './Button.module.scss';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'outline'
  | 'ghost';

export type ButtonSize = 'sm' | 'md' | 'lg';

type ButtonBaseProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  loading?: boolean;
  /** Visually hidden text announced by screen readers during loading. Pass a translated string. */
  loadingText?: string;
  fullWidth?: boolean;
  /** Required test anchor for Playwright and unit tests. Applied directly on the clickable element. */
  'data-testid': string;
};

/**
 * When `iconOnly` is true, `aria-label` is required to provide an accessible name
 * for screen readers (no visible label text is rendered).
 */
export type ButtonProps = ButtonBaseProps &
  (
    | {
        iconOnly: true;
        'aria-label': string;
      }
    | { iconOnly?: false }
  );

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'lg',
      iconLeft,
      iconRight,
      iconOnly = false,
      loading = false,
      loadingText = 'Loading…',
      fullWidth = false,
      className,
      children,
      disabled,
      // Default to "button" to prevent accidental form submission (WCAG 4.1.2)
      type = 'button',
      ...props
    },
    ref,
  ) => {
    // Dev-time guard: icon-only buttons must have an accessible name
    if (import.meta.env.DEV && iconOnly) {
      const label = (props as { 'aria-label'?: string })['aria-label'];
      if (!label) {
        console.warn(
          '[Button] Icon-only buttons require an `aria-label` prop for screen readers (WCAG 2.4.6).',
        );
      }
    }

    const classNames = [
      styles.button,
      styles[`button--${variant}`],
      styles[`button--${size}`],
      iconOnly && styles['button--icon-only'],
      fullWidth && styles['button--full-width'],
      loading && styles['button--loading'],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        className={classNames}
        disabled={disabled || loading}
        type={type}
        aria-busy={loading || undefined}
        aria-disabled={disabled || loading || undefined}
        {...props}
      >
        {loading && (
          <>
            <span className={styles.spinner} aria-hidden="true">
              <span className={styles['spinner-circle']} />
            </span>
            {/* Announced by screen readers instead of the hidden button label */}
            <span className={styles['sr-only']}>{loadingText}</span>
          </>
        )}
        {!loading && iconOnly && (
          <span className={styles.icon} aria-hidden="true">
            {iconLeft || iconRight || children}
          </span>
        )}
        {!loading && !iconOnly && (
          <>
            {iconLeft && (
              <span className={styles['icon-left']} aria-hidden="true">
                {iconLeft}
              </span>
            )}
            <span className={styles.label}>{children}</span>
            {iconRight && (
              <span className={styles['icon-right']} aria-hidden="true">
                {iconRight}
              </span>
            )}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
